import { HAS_WINDOW } from '@eventbrite/feature-detection';
import React, { useCallback, useEffect, useState } from 'react';
import { useEventBasicInformationContext } from '../../../contexts';
import { useListingTimedEntryExperimentContext } from '../../../contexts/ListingTimedEntryExperimentContext';
import { LocalizedDateTime } from '../../../types';
import { LocationProps } from '../WhenAndWhere/Location/Location';
import { StartDateAndLocationView } from './StartDateAndLocationView';
import {
    getFormattedDateRange,
    getFormattedStartDate,
    getMultipleDatesFlag,
    isSameDayInTimezone,
} from './utils';

export type StartDateAndLocationContainerProps = {
    isOnlineEvent: boolean;
    isSeriesParent: boolean;
    isRepeating: boolean;
    locale: string;
    location?: LocationProps;
};

type FormattedDates = {
    formattedDateTime: string;
    dateTime: string;
} | null;

const formatEventDates = (
    start: LocalizedDateTime,
    end: LocalizedDateTime | null | undefined,
    isOnlineEvent: boolean,
    locale: string,
    hideEndDate = false,
): FormattedDates => {
    const shouldShowSingleDate =
        !end || isSameDayInTimezone(start, end, isOnlineEvent) || hideEndDate;

    if (shouldShowSingleDate) {
        return getFormattedStartDate({
            eventStartDateTime: start,
            eventEndDateTime: end,
            isOnlineEvent,
            locale,
            hideEndDate,
        });
    }

    return getFormattedDateRange({
        eventStartDateTime: start,
        eventEndDateTime: end,
        isOnlineEvent,
        locale,
    });
};

const useDateFormatting = (
    startDateTime: LocalizedDateTime | null | undefined,
    endDateTime: LocalizedDateTime | null | undefined,
    isOnlineEvent: boolean,
    locale: string,
    hideEndDate = false,
) => {
    const getFormattedDatesForEvent = useCallback(
        (
            start: LocalizedDateTime,
            end: LocalizedDateTime | null | undefined,
        ): FormattedDates => {
            return formatEventDates(
                start,
                end,
                isOnlineEvent,
                locale,
                hideEndDate,
            );
        },
        [isOnlineEvent, locale, hideEndDate],
    );

    const [formattedDates, setFormattedDates] = useState<FormattedDates>(null);

    useEffect(() => {
        if (startDateTime) {
            setFormattedDates(
                getFormattedDatesForEvent(startDateTime, endDateTime),
            );
        }
    }, [startDateTime, endDateTime, getFormattedDatesForEvent]);

    return formattedDates;
};

const setI18nLocale = (locale: string) => {
    if (HAS_WINDOW) {
        window.__i18n__ = { locale };
    }
};

export const StartDateAndLocationContainer = ({
    isOnlineEvent,
    locale,
    location,
    isSeriesParent,
    isRepeating,
}: StartDateAndLocationContainerProps) => {
    const eventBasicInformation = useEventBasicInformationContext();
    const { isListingTimedEntryExperimentActive } =
        useListingTimedEntryExperimentContext();

    const formattedDates = useDateFormatting(
        eventBasicInformation?.start,
        eventBasicInformation?.end,
        isOnlineEvent,
        locale,
        eventBasicInformation?.hideEndDate,
    );

    const isMultipleDates = getMultipleDatesFlag(
        isListingTimedEntryExperimentActive,
        isSeriesParent,
        isRepeating,
    );

    setI18nLocale(locale);

    return (
        <StartDateAndLocationView
            dateTimeText={formattedDates?.dateTime}
            dateText={formattedDates?.formattedDateTime}
            location={location}
            isSeriesParent={isSeriesParent}
            isMultipleDates={isMultipleDates}
        />
    );
};
